/**
 * 问卷状态管理 Slice
 * 问卷填写、提交、评分管理
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { apiSlice } from '../api/apiSlice';

// 问卷类型枚举
export enum QuestionnaireType {
  INITIAL_SCREENING = 'initial_screening',
  DETAILED_ASSESSMENT = 'detailed_assessment',
}

// 问卷状态枚举
export enum QuestionnaireStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  UNDER_REVIEW = 'under_review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  REVISION_REQUIRED = 'revision_required',
}

// 风险等级枚举
export enum RiskLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  VERY_HIGH = 'very_high',
}

// 问卷数据接口
export interface Questionnaire {
  id: string;
  userId: string;
  type: QuestionnaireType;
  status: QuestionnaireStatus;
  answers: Record<string, any>;
  attachments: string[];
  tags: string[];
  metadata: {
    ipAddress?: string;
    userAgent?: string;
    completionTime?: number;
  };
  submittedAt?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewComments?: string;
  score: {
    overall: number;
    financial: number;
    environmental: number;
    social: number;
    governance: number;
    risk: number;
    compliance: number;
  };
  riskLevel: RiskLevel;
  createdAt: string;
  updatedAt: string;
}

// 问卷步骤接口
export interface QuestionnaireStep {
  id: string;
  title: string;
  description: string;
  questions: Question[];
  estimatedTime: number;
  isCompleted: boolean;
  completedAt?: string;
}

// 问题接口
export interface Question {
  id: string;
  type:
    | 'single_choice'
    | 'multiple_choice'
    | 'select'
    | 'input'
    | 'textarea'
    | 'slider';
  title: string;
  description?: string;
  required: boolean;
  options?: QuestionOption[];
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
  };
  placeholder?: string;
  unit?: string;
  conditionalDisplay?: {
    dependsOn: string;
    values: any[];
  };
}

// 问题选项接口
export interface QuestionOption {
  value: string;
  label: string;
  description?: string;
}

// 问卷状态接口
export interface QuestionnaireState {
  // 当前问卷
  currentQuestionnaire: Questionnaire | null;

  // 问卷列表
  questionnaires: Questionnaire[];

  // 当前步骤
  currentStep: number;
  steps: QuestionnaireStep[];

  // 答案缓存
  answers: Record<string, any>;

  // 状态标识
  isLoading: boolean;
  isSaving: boolean;
  isSubmitting: boolean;

  // 错误信息
  error: string | null;

  // 进度信息
  progress: {
    completed: number;
    total: number;
    percentage: number;
  };

  // 自动保存
  autoSaveEnabled: boolean;
  lastSaved: number | null;
}

// 初始状态
const initialState: QuestionnaireState = {
  currentQuestionnaire: null,
  questionnaires: [],
  currentStep: 0,
  steps: [],
  answers: {},
  isLoading: false,
  isSaving: false,
  isSubmitting: false,
  error: null,
  progress: {
    completed: 0,
    total: 0,
    percentage: 0,
  },
  autoSaveEnabled: true,
  lastSaved: null,
};

// 问卷slice
const questionnaireSlice = createSlice({
  name: 'questionnaire',
  initialState,
  reducers: {
    // 设置加载状态
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // 设置保存状态
    setSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload;
    },

    // 设置提交状态
    setSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },

    // 设置错误
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // 清除错误
    clearError: state => {
      state.error = null;
    },

    // 设置当前问卷
    setCurrentQuestionnaire: (state, action: PayloadAction<Questionnaire>) => {
      state.currentQuestionnaire = action.payload;
      state.answers = action.payload.answers || {};
    },

    // 设置步骤
    setSteps: (state, action: PayloadAction<QuestionnaireStep[]>) => {
      state.steps = action.payload;
      state.progress.total = action.payload.length;
      state.progress.completed = action.payload.filter(
        step => step.isCompleted
      ).length;
      state.progress.percentage = Math.round(
        (state.progress.completed / state.progress.total) * 100
      );
    },

    // 设置当前步骤
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },

    // 下一步
    nextStep: state => {
      if (state.currentStep < state.steps.length - 1) {
        state.currentStep += 1;
      }
    },

    // 上一步
    previousStep: state => {
      if (state.currentStep > 0) {
        state.currentStep -= 1;
      }
    },

    // 更新答案
    updateAnswer: (
      state,
      action: PayloadAction<{ questionId: string; value: any }>
    ) => {
      const { questionId, value } = action.payload;
      state.answers[questionId] = value;

      // 更新最后保存时间
      if (state.autoSaveEnabled) {
        state.lastSaved = Date.now();
      }
    },

    // 批量更新答案
    updateAnswers: (state, action: PayloadAction<Record<string, any>>) => {
      state.answers = { ...state.answers, ...action.payload };

      if (state.autoSaveEnabled) {
        state.lastSaved = Date.now();
      }
    },

    // 完成步骤
    completeStep: (state, action: PayloadAction<number>) => {
      const stepIndex = action.payload;
      if (state.steps[stepIndex]) {
        state.steps[stepIndex].isCompleted = true;
        state.steps[stepIndex].completedAt = new Date().toISOString();

        // 更新进度
        state.progress.completed = state.steps.filter(
          step => step.isCompleted
        ).length;
        state.progress.percentage = Math.round(
          (state.progress.completed / state.progress.total) * 100
        );
      }
    },

    // 重置问卷
    resetQuestionnaire: state => {
      state.currentQuestionnaire = null;
      state.currentStep = 0;
      state.steps = [];
      state.answers = {};
      state.error = null;
      state.progress = {
        completed: 0,
        total: 0,
        percentage: 0,
      };
      state.lastSaved = null;
    },

    // 设置自动保存
    setAutoSave: (state, action: PayloadAction<boolean>) => {
      state.autoSaveEnabled = action.payload;
    },
  },
});

// 扩展API slice - 问卷相关端点
export const questionnaireApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    // 获取问卷列表
    getQuestionnaires: builder.query<
      GetQuestionnairesResponse,
      GetQuestionnairesRequest
    >({
      query: params => ({
        url: '/questionnaires',
        params,
      }),
      providesTags: ['Questionnaire'],
    }),

    // 获取单个问卷
    getQuestionnaire: builder.query<GetQuestionnaireResponse, string>({
      query: id => `/questionnaire/${id}`,
      providesTags: (result, error, id) => [{ type: 'Questionnaire', id }],
    }),

    // 创建问卷
    createQuestionnaire: builder.mutation<
      CreateQuestionnaireResponse,
      CreateQuestionnaireRequest
    >({
      query: data => ({
        url: '/questionnaire/create',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Questionnaire'],
    }),

    // 提交问卷
    submitQuestionnaire: builder.mutation<
      SubmitQuestionnaireResponse,
      SubmitQuestionnaireRequest
    >({
      query: ({ id, ...data }) => ({
        url: `/questionnaire/${id}/submit`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Questionnaire', id },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        dispatch(questionnaireSlice.actions.setSubmitting(true));
        try {
          const { data } = await queryFulfilled;
          if (data.success) {
            dispatch(questionnaireSlice.actions.setSubmitting(false));
            dispatch(questionnaireSlice.actions.clearError());
          }
        } catch (error: any) {
          dispatch(
            questionnaireSlice.actions.setError(
              error.error?.data?.message || '提交失败，请重试'
            )
          );
          dispatch(questionnaireSlice.actions.setSubmitting(false));
        }
      },
    }),

    // 保存问卷草稿
    saveDraft: builder.mutation<SaveDraftResponse, SaveDraftRequest>({
      query: ({ id, answers }) => ({
        url: `/questionnaire/${id}/draft`,
        method: 'PUT',
        body: { answers },
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        dispatch(questionnaireSlice.actions.setSaving(true));
        try {
          await queryFulfilled;
          dispatch(questionnaireSlice.actions.setSaving(false));
          dispatch(questionnaireSlice.actions.clearError());
        } catch (error: any) {
          dispatch(
            questionnaireSlice.actions.setError(
              error.error?.data?.message || '保存失败'
            )
          );
          dispatch(questionnaireSlice.actions.setSaving(false));
        }
      },
    }),

    // 获取问卷统计
    getQuestionnaireStats: builder.query<QuestionnaireStatsResponse, void>({
      query: () => '/questionnaire/stats',
      providesTags: ['Questionnaire'],
    }),
  }),
});

// 导出actions和hooks
export const {
  setLoading,
  setSaving,
  setSubmitting,
  setError,
  clearError,
  setCurrentQuestionnaire,
  setSteps,
  setCurrentStep,
  nextStep,
  previousStep,
  updateAnswer,
  updateAnswers,
  completeStep,
  resetQuestionnaire,
  setAutoSave,
} = questionnaireSlice.actions;

// 注意：问卷相关的API hooks现在从 questionnaireApi.ts 导出
// 这里只保留非冲突的查询hooks
export const {
  useGetQuestionnairesQuery: useGetQuestionnairesQueryOld,
  useGetQuestionnaireQuery: useGetQuestionnaireQueryOld,
  // useCreateQuestionnaireMutation, // 已移除，使用questionnaireApi.ts中的版本
  // useSubmitQuestionnaireMutation, // 已移除，使用questionnaireApi.ts中的版本
  useSaveDraftMutation,
  useGetQuestionnaireStatsQuery,
} = questionnaireApiSlice;

export default questionnaireSlice.reducer;

// TypeScript类型定义
export interface GetQuestionnairesRequest {
  type?: QuestionnaireType;
  status?: QuestionnaireStatus;
  page?: number;
  limit?: number;
}

export interface GetQuestionnairesResponse {
  success: boolean;
  data: {
    questionnaires: Questionnaire[];
    total: number;
    page: number;
    limit: number;
  };
}

export interface GetQuestionnaireResponse {
  success: boolean;
  data: Questionnaire;
}

export interface CreateQuestionnaireRequest {
  type: QuestionnaireType;
  answers?: Record<string, any>;
}

export interface CreateQuestionnaireResponse {
  success: boolean;
  data: Questionnaire;
}

export interface SubmitQuestionnaireRequest {
  id: string;
  answers: Record<string, any>;
  attachments?: string[];
}

export interface SubmitQuestionnaireResponse {
  success: boolean;
  data: Questionnaire;
}

export interface SaveDraftRequest {
  id: string;
  answers: Record<string, any>;
}

export interface SaveDraftResponse {
  success: boolean;
  message: string;
}

export interface QuestionnaireStatsResponse {
  success: boolean;
  data: {
    total: number;
    byStatus: Record<QuestionnaireStatus, number>;
    byType: Record<QuestionnaireType, number>;
    averageScore: number;
    completionRate: number;
  };
}
